import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Typography,
  Alert,
  Collapse,
  Upload,
  message,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import {
  BugOutlined,
  ToolOutlined,
  BookOutlined,
  SettingOutlined,
  SendOutlined,
  CustomerServiceOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { Confetti } from '@neoconfetti/react';
import { useAuth } from '../../../hooks/useAuth';
import { logger } from '../../../utils/logger';
import {
  createSupportTicket,
  SUPPORT_CATEGORY_ID,
  SUPPORT_PORTAL_URL,
  SUBCATEGORIES,
} from './supportApi';
import type { SubcategoryId } from './supportApi';
import type { ErrorContext } from '../../../stores/useSupportModalStore';
import { formatErrorMetadata, formatErrorMetadataCompact } from '../../../utils/openSupportWithError';
import { validateFile, convertFilesToAttachments } from '../../../utils/fileUtils';

import './SupportTicketModal.scss';

const { Text } = Typography;
const { TextArea } = Input;

const MODAL_WIDTH = 620;
const CONFETTI_PARTICLE_COUNT = 150;
const CONFETTI_STAGE_WIDTH = 1200;
const CONFETTI_STAGE_HEIGHT = 1400;

interface SubcategoryMeta {
  icon: React.ReactNode;
  color: string;
  hint: string;
}

const SUBCATEGORY_META: Record<SubcategoryId, SubcategoryMeta> = {
  'ca894a86-bc32-405f-89ec-35c8f9df6104': {
    icon: <BugOutlined />,
    color: 'var(--color-error)',
    hint: 'Что-то работает неправильно',
  },
  '8266204b-f283-4557-8734-bb056c29a34c': {
    icon: <ToolOutlined />,
    color: 'var(--color-info)',
    hint: 'Улучшение функционала',
  },
  'f5cfe35b-63b1-4e92-bda8-f8e3f5a47cc3': {
    icon: <BookOutlined />,
    color: 'var(--color-accent)',
    hint: 'Помощь и обучение',
  },
  'cf0b6034-238f-4dff-9513-83f4bcb6ff43': {
    icon: <SettingOutlined />,
    color: 'var(--color-success)',
    hint: 'Услуга, права, доступы',
  },
};

interface FormValues {
  title: string;
  subcategoryId: string;
  description: string;
}

interface SupportTicketModalProps {
  open: boolean;
  onClose: () => void;
  initialValues?: ErrorContext | null;
}

/* ─── Success ──────────────────────────────────────────────────────────── */

const SuccessView: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <motion.div
    className="stm-success"
    initial={{ opacity: 0, scale: 0.92 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="stm-success__confetti">
      <Confetti
        particleCount={CONFETTI_PARTICLE_COUNT}
        force={0.7}
        stageHeight={CONFETTI_STAGE_HEIGHT}
        stageWidth={CONFETTI_STAGE_WIDTH}
        colors={['#4f46e5', '#7c3aed', '#2563eb', '#06b6d4', '#10b981', '#f59e0b', '#ef4444']}
      />
    </div>

    <motion.div
      className="stm-success__ring"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 18 }}
    >
      <svg viewBox="0 0 52 52" className="stm-success__check">
        <motion.path
          d="M14 27l7.8 7.8L38 17"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ delay: 0.4, duration: 0.4, ease: 'easeOut' }}
        />
      </svg>
    </motion.div>

    <motion.div
      className="stm-success__text"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35, duration: 0.35 }}
    >
      <Text strong className="stm-success__title">
        Заявка успешно создана
      </Text>
      <Text type="secondary" className="stm-success__desc">
        Мы уже работаем над вашим обращением
        <br />и свяжемся с вами в ближайшее время
      </Text>
    </motion.div>

    <motion.div
      className="stm-success__actions"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.6 }}
    >
      {SUPPORT_PORTAL_URL && (
        <a
          href={SUPPORT_PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="stm-success__portal-link"
        >
          Следить за заявками <ExportOutlined />
        </a>
      )}
      <Button
        type="primary"
        onClick={onClose}
        size="large"
        className="stm-success__btn"
      >
        Закрыть
      </Button>
    </motion.div>
  </motion.div>
);

/* ─── Category card ────────────────────────────────────────────────────── */

interface CategoryCardProps {
  id: SubcategoryId;
  label: string;
  selected: boolean;
  onSelect: () => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ id, label, selected, onSelect }) => {
  const meta = SUBCATEGORY_META[id];
  return (
    <button
      type="button"
      className={`stm-cat-card ${selected ? 'stm-cat-card--active' : ''}`}
      onClick={onSelect}
    >
      <span
        className="stm-cat-card__icon"
        style={{ color: meta.color, background: `${meta.color}10` }}
      >
        {meta.icon}
      </span>
      <span className="stm-cat-card__text">
        <span className="stm-cat-card__label">{label}</span>
        <span className="stm-cat-card__hint">{meta.hint}</span>
      </span>
    </button>
  );
};

const CategoryCardGroup: React.FC<{
  value?: string;
  onChange?: (val: string) => void;
}> = ({ value, onChange }) => (
  <div className="stm-cat-grid">
    {SUBCATEGORIES.map((sc) => (
      <CategoryCard
        key={sc.id}
        id={sc.id}
        label={sc.label}
        selected={value === sc.id}
        onSelect={() => onChange?.(sc.id)}
      />
    ))}
  </div>
);

/* ─── Modal ────────────────────────────────────────────────────────────── */

export const SupportTicketModal: React.FC<SupportTicketModalProps> = ({
  open,
  onClose,
  initialValues,
}) => {
  const [form] = Form.useForm<FormValues>();
  const { account, user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const fileListRef = useRef<UploadFile[]>(fileList);
  const [attachHovered, setAttachHovered] = useState(false);

  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  const hasInitialValues = !!(initialValues?.title || initialValues?.description);

  useEffect(() => {
    if (open && initialValues) {
      const fieldsToSet: Partial<FormValues> = {};
      if (initialValues.subcategoryId) fieldsToSet.subcategoryId = initialValues.subcategoryId;
      if (initialValues.title) fieldsToSet.title = initialValues.title;
      if (initialValues.description) fieldsToSet.description = initialValues.description;
      form.setFieldsValue(fieldsToSet);
    }
  }, [open, initialValues, form]);

  // Единый источник личности — активный MSAL-аккаунт (UPN из ID-токена).
  // Это исключает рассинхрон с Graph /me (его ответ может задерживаться,
  // падать и оставлять в стейте профиль предыдущего пользователя) и
  // расхождение между userPrincipalName и mail в AzureAD.
  // Fallback на Graph-профиль — только если MSAL-аккаунт по какой-то
  // причине ещё не готов.
  const userEmail = useMemo(
    () => account?.username || user?.userPrincipalName || user?.mail || '',
    [account, user],
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (!userEmail) {
        message.error('Не удалось определить ваш email. Попробуйте перезайти.');
        return;
      }
      // Диагностика: если UPN из Graph не совпадает с UPN из MSAL-аккаунта —
      // логируем, чтобы отследить потенциальный рассинхрон (но не блокируем,
      // так как у AzureAD UPN и mail бывают штатно разными, а авторитетным
      // источником мы всё равно считаем MSAL).
      const graphUpn = user?.userPrincipalName?.toLowerCase();
      const msalUpn = account?.username?.toLowerCase();
      if (graphUpn && msalUpn && graphUpn !== msalUpn) {
        logger.warn('Расхождение UPN между MSAL и Graph при создании заявки', {
          category: 'api',
          data: { msalUpn, graphUpn },
        });
      }

      // Дописываем метаданные ошибки к описанию, вписываясь в лимит 500 символов
      const DESCRIPTION_LIMIT = 500;
      let fullDescription = values.description;
      if (initialValues?.metadata) {
        const budget = DESCRIPTION_LIMIT - fullDescription.length;
        const metaText = formatErrorMetadataCompact(initialValues.metadata, budget);
        fullDescription += metaText;
      }
      fullDescription = fullDescription.slice(0, DESCRIPTION_LIMIT);

      setSubmitting(true);
      try {
        logger.info('Создание заявки в поддержку', {
          category: 'api',
          data: {
            createdByEmail: userEmail,
            accountUsername: account?.username,
            accountOid: account?.localAccountId,
            graphUpn: user?.userPrincipalName,
            graphMail: user?.mail,
          },
        });
        const attachments = await convertFilesToAttachments(
          fileList.reduce<File[]>((acc, f) => {
            if (f.originFileObj) acc.push(f.originFileObj);
            return acc;
          }, []),
        );

        await createSupportTicket({
          title: values.title,
          description: fullDescription,
          categoryId: SUPPORT_CATEGORY_ID,
          subcategoryId: values.subcategoryId,
          createdByEmail: userEmail,
          attachments,
        });
        setSubmitted(true);
      } catch {
        message.error('Не удалось отправить заявку. Попробуйте позже.');
      } finally {
        setSubmitting(false);
      }
    },
    [
      userEmail,
      initialValues,
      fileList,
      account?.username,
      account?.localAccountId,
      user?.userPrincipalName,
      user?.mail,
    ],
  );

  const handleClose = useCallback(() => {
    form.resetFields();
    setSubmitted(false);
    setFileList([]);
    onClose();
  }, [form, onClose]);

  const beforeUpload: UploadProps['beforeUpload'] = useCallback((file: File) => {
    const error = validateFile(file);
    if (error) {
      message.error(`${file.name}: ${error}`);
      return Upload.LIST_IGNORE;
    }
    return false;
  }, []);

  const handleUploadChange = useCallback(
    (info: Parameters<NonNullable<UploadProps['onChange']>>[0]) => {
      setFileList(info.fileList);
    },
    [],
  );

  useEffect(() => {
    if (!attachHovered || submitting) return undefined;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const pastedFiles: File[] = [];
      Array.from(items).forEach((item) => {
        if (item.kind !== 'file') return;
        const file = item.getAsFile();
        if (file) pastedFiles.push(file);
      });

      if (pastedFiles.length === 0) return;
      e.preventDefault();

      const accepted: UploadFile[] = [];
      pastedFiles.forEach((file, index) => {
        let namedFile = file;
        if (!file.name) {
          const subtype = file.type.split('/')[1] || 'png';
          const ext = subtype.split('+')[0];
          namedFile = new File([file], `image-${Date.now()}.${ext}`, { type: file.type });
        }

        const uid = `paste-${Date.now()}-${index}`;
        const rcFile = namedFile as unknown as RcFile;
        rcFile.uid = uid;

        const beforeResult = beforeUpload(rcFile, pastedFiles as unknown as RcFile[]);
        if (beforeResult === Upload.LIST_IGNORE) return;

        accepted.push({
          uid,
          name: namedFile.name,
          status: 'done',
          originFileObj: rcFile,
        });
      });

      if (accepted.length === 0) return;
      const nextFileList = [...fileListRef.current, ...accepted];
      setFileList(nextFileList);
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [attachHovered, submitting, beforeUpload]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={MODAL_WIDTH}
      destroyOnHidden
      centered
      className="stm-modal"
      title={null}
      closeIcon={submitted ? null : undefined}
    >
      <AnimatePresence mode="wait" initial={false}>
        {submitted ? (
          <SuccessView key="success" onClose={handleClose} />
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* ── Hero ─────────────────────────────────────── */}
            <div className="stm-hero">
              <div className="stm-hero__icon-wrap">
                <CustomerServiceOutlined />
              </div>
              <div className="stm-hero__copy">
                <Text strong className="stm-hero__title">
                  Обращение в поддержку
                </Text>
                <Text type="secondary" className="stm-hero__subtitle">
                  Опишите проблему — ответим как можно скорее
                </Text>
              </div>
            </div>

            <div className="stm-divider" />

            {hasInitialValues && (
              <Alert
                type="info"
                showIcon
                message="Заявка создана из контекста ошибки. Вы можете дополнить описание."
                style={{ marginBottom: 16 }}
              />
            )}

            {initialValues?.metadata && (
              <Collapse
                ghost
                size="small"
                style={{ marginBottom: 12, background: 'var(--color-bg-primary)', borderRadius: 6 }}
                items={[
                  {
                    key: 'meta',
                    label: (
                      <span style={{ fontSize: 12, color: 'var(--color-text-disabled)' }}>
                        <InfoCircleOutlined style={{ marginRight: 6 }} />
                        Техническая информация (будет отправлена автоматически)
                      </span>
                    ),
                    children: (
                      <pre style={{
                        fontSize: 11,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: 'var(--color-text-secondary)',
                        maxHeight: 200,
                        overflow: 'auto',
                      }}>
                        {formatErrorMetadata(initialValues.metadata)}
                      </pre>
                    ),
                  },
                ]}
              />
            )}

            {/* ── Form ─────────────────────────────────────── */}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              requiredMark={false}
              initialValues={{
                subcategoryId: SUBCATEGORIES[0].id,
              }}
              className="stm-form"
            >
              <Form.Item name="subcategoryId" label="Тип обращения">
                <CategoryCardGroup />
              </Form.Item>

              <Form.Item
                name="title"
                label="Тема"
                rules={[
                  { required: true, message: 'Укажите тему заявки' },
                  { min: 5, message: 'Минимум 5 символов' },
                ]}
              >
                <Input
                  placeholder="Кратко опишите суть обращения"
                  maxLength={100}
                  size="large"
                  autoFocus
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="Описание"
                rules={[
                  { required: true, message: 'Добавьте описание' },
                  { min: 10, message: 'Минимум 10 символов' },
                ]}
              >
                <TextArea
                  placeholder="Подробно опишите, что произошло и какие шаги привели к проблеме"
                  rows={5}
                  maxLength={500}
                  showCount
                  style={{ resize: 'vertical', maxHeight: '40vh' }}
                />
              </Form.Item>

              <Form.Item label="Вложения" className="stm-attachments">
                <div
                  onMouseEnter={() => setAttachHovered(true)}
                  onMouseLeave={() => setAttachHovered(false)}
                >
                  <Upload
                    multiple
                    fileList={fileList}
                    beforeUpload={beforeUpload}
                    onChange={handleUploadChange}
                    listType="text"
                  >
                    <Button icon={<PaperClipOutlined />}>Прикрепить файл</Button>
                  </Upload>
                </div>
                <Text type="secondary" className="stm-attachments__hint">
                  До 10МБ на файл. Изображения, PDF, DOC/XLS/PPT, TXT, CSV, ZIP/RAR/7z. Можно вставить из буфера (Ctrl+V)
                </Text>
              </Form.Item>

              {/* ── Action bar ─────────────────────────────── */}
              <div className="stm-action-bar">
                <div className="stm-action-bar__buttons">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    icon={!submitting ? <SendOutlined /> : undefined}
                    className="stm-submit"
                    block
                  >
                    {submitting ? 'Отправка...' : 'Отправить заявку'}
                  </Button>
                  <button
                    type="button"
                    className="stm-cancel-link"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </Form>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};
